import React, { useEffect, useState } from 'react';

import { softwareBacklogConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-backlog/src/softwareBacklogConfigProviderEntrypoint.entrypoint.tsx';
import { softwareVersionDetailConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-version-detail/src/softwareVersionDetailConfigProviderEntrypoint.entrypoint.tsx';
import { projectVersionsConfigProviderEntrypoint } from '@jira/ai-rovo_actions-project-versions/src/projectVersionsConfigProviderEntrypoint.entrypoint.tsx';
import { issueConfigProviderEntrypoint } from '@jira/ai-rovo_actions-issue/src/issueConfigProviderEntrypoint.entrypoint.tsx';
import { planTimelineConfigProviderEntrypoint } from '@jira/ai-rovo_actions-plan-timeline/src/planTimelineConfigProviderEntrypoint.entrypoint.tsx';
import { globalWorkTypesSettingsConfigProviderEntrypoint } from '@jira/ai-rovo__actions-global-settings-work-items/src/globalWorkTypesSettingsConfigProviderEntrypoint.entrypoint.tsx';
import { permissionsConfigProviderEntrypoint } from '@jira/ai-rovo__actions-permissions/src/permissionsConfigProviderEntrypoint.entrypoint.tsx';
import { slaConfigProviderEntrypoint } from '@jira/ai-rovo__actions-permissions/src/slaConfigProviderEntrypoint.entrypoint.tsx';
import { spacePermissionConfigProviderEntrypoint } from '@jira/ai-rovo__actions-permissions/src/spacePermissionConfigProviderEntrypoint.entrypoint.tsx';
import { projectSettingsDetailsConfigProviderEntrypoint } from '@jira/ai-rovo__actions-project-settings/src/projectSettingsDetailsConfigProviderEntrypoint.entrypoint.tsx';
import { listConfigProviderEntrypoint } from '@jira/ai-rovo__actions-software-list/src/listConfigProviderEntrypoint.entrypoint.tsx';
import { onboardingConfigProviderListEntrypoint } from '@jira/ai-rovo__jira-find-onboarding/src/onboardingConfigProviderListEntrypoint.entrypoint.tsx';
import { onboardingConfigProviderBoardEntrypoint } from '@jira/ai-rovo__jira-find-onboarding/src/onboardingConfigProviderBoardEntrypoint.entrypoint.tsx';
import { onboardingConfigProviderBacklogEntrypoint } from '@jira/ai-rovo__jira-find-onboarding/src/onboardingConfigProviderBacklogEntrypoint.entrypoint.tsx';
import { jiraRovoSearchConfigProviderEntrypoint } from '@jira/ai-rovo__actions-jira-rovo-search/src/jiraRovoSearchConfigProviderEntrypoint.entrypoint.tsx';
import { schedulesRovoConfigProviderEntrypoint } from '@jira/servicedesk__schedules/src/schedulesRovoConfigProviderEntrypoint.entrypoint.tsx';
import { availabilityRovoConfigProviderEntrypoint } from '@jira/servicedesk__availability/src/availabilityRovoConfigProviderEntrypoint.entrypoint.tsx';
import { capacityRovoConfigProviderEntrypoint } from '@jira/servicedesk__capacity/src/capacityRovoConfigProviderEntrypoint.entrypoint.tsx';
import { requestTypesConfigProviderEntrypoint } from '@jira/ai-rovo__actions-jsm-gs/src/requestTypesConfigProviderEntrypoint.entrypoint.tsx';
import { queuesConfigProviderEntrypoint } from '@jira/ai-rovo__actions-jsm-gs/src/queuesConfigProviderEntrypoint.entrypoint.tsx';
import { slaGsConfigProviderEntrypoint } from '@jira/ai-rovo__actions-jsm-gs/src/slaGsConfigProviderEntrypoint.entrypoint.tsx';
import { jpdProjectViewConfigProviderEntrypoint } from '@jira/ai-rovo__actions-jpd-project-view/src/jpdProjectViewConfigProviderEntrypoint.entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	addRouteListener,
	getRouterState,
} from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import { useIsFirstTwoWeeks } from './useIsFirstTwoWeeks.tsx';
import type { RouterState } from '@atlassian/react-resource-router/src/controllers/router-store/types.tsx';
import { AiRovoFomoNudgeProviderDeferring } from './AiRovoFomoNudgeProvider.tsx';
import { AiRovoIssueViewNudgesProvider } from './AiRovoIssueViewNudgesProvider.tsx';
import { AiRovoProjectScopedNudgeSubscriptionProvider } from './AiRovoProjectScopedNudgeSubscriptionProvider.tsx';
import { AiRovoPermissionsNudgesProvider } from './AiRovoPermissionsNudgesProvider.tsx';
import { AiRovoSlaNudgesProvider } from './AiRovoSlaNudgesProvider.tsx';
import { AiRovoSpacePermissionNudgesProvider } from './AiRovoSpacePermissionNudgesProvider.tsx';
import { AiRovoNavigationNudgesProvider } from './AiRovoNavigationNudgesProvider.tsx';
import { AiRovoProjectSettingDetailNudgesProvider } from './AiRovoProjectSettingDetailNudgesProvider.tsx';
import { AiRovoAiSpaceCreationNudgesProvider } from './AiRovoAiSpaceCreationNudgesProvider.tsx';
import { AiRovoJiraRovoSearchNudgesProvider } from './AiRovoJiraRovoSearchNudgesProvider.tsx';
import { AiRovoSchedulesNudgesProvider } from './AiRovoSchedulesNudgesProvider.tsx';
import { AiRovoAvailabilityNudgesProvider } from './AiRovoAvailabilityNudgesProvider.tsx';
import { AiRovoCapacityNudgesProvider } from './AiRovoCapacityNudgesProvider.tsx';
import { useIsRovoForJiraOnboardingEnabled } from './useIsRovoForJiraOnboardingEnabled.tsx';

/**
 * Add new entrypoints here to the type and to the jiraRouteToConfigProvider object
 *
 * NOTE:
 * You can use any string as a key for the configuration, and base it off of the route name or the query parameters.
 * In the future, you will also be able to set context specific configuration for each route, and link
 * that to the appropriate key for the configuration.
 */
export type RovoEntryPointComponent =
	| typeof softwareBacklogConfigProviderEntrypoint
	| typeof softwareVersionDetailConfigProviderEntrypoint
	| typeof projectVersionsConfigProviderEntrypoint
	| typeof issueConfigProviderEntrypoint
	| typeof planTimelineConfigProviderEntrypoint
	| typeof permissionsConfigProviderEntrypoint
	| typeof slaConfigProviderEntrypoint
	| typeof spacePermissionConfigProviderEntrypoint
	| typeof globalWorkTypesSettingsConfigProviderEntrypoint
	| typeof projectSettingsDetailsConfigProviderEntrypoint
	| typeof listConfigProviderEntrypoint
	| typeof onboardingConfigProviderListEntrypoint
	| typeof onboardingConfigProviderBoardEntrypoint
	| typeof onboardingConfigProviderBacklogEntrypoint
	| typeof jiraRovoSearchConfigProviderEntrypoint
	| typeof schedulesRovoConfigProviderEntrypoint
	| typeof availabilityRovoConfigProviderEntrypoint
	| typeof capacityRovoConfigProviderEntrypoint
	| typeof requestTypesConfigProviderEntrypoint
	| typeof queuesConfigProviderEntrypoint
	| typeof slaGsConfigProviderEntrypoint
	| typeof jpdProjectViewConfigProviderEntrypoint;

export const jiraRouteToConfigProvider: Partial<
	Record<
		string,
		{
			entrypoint: RovoEntryPointComponent;
			nudges?: () => React.JSX.Element;
		}
	>
> = {
	'software-backlog': {
		entrypoint: softwareBacklogConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoProjectScopedNudgeSubscriptionProvider sourceId="nudges-software-backlog" />
			</>
		),
	},
	'software-boards': {
		entrypoint: softwareBacklogConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoProjectScopedNudgeSubscriptionProvider sourceId="nudges-software-boards" />
			</>
		),
	},
	'software-version-detail': {
		entrypoint: softwareVersionDetailConfigProviderEntrypoint,
	},
	'project-versions': {
		entrypoint: projectVersionsConfigProviderEntrypoint,
	},
	issue: {
		entrypoint: issueConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoIssueViewNudgesProvider />
			</>
		),
	},
	'software-plan-timeline': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'software-plan-calendar': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'software-plan-increment': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'software-plan-increments': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'software-plan-releases': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'software-plan-dependencies': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'plan-settings-features': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'plan-settings-issue-sources': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'plan-settings-program-boards': {
		entrypoint: planTimelineConfigProviderEntrypoint,
	},
	'project-settings-software-access': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoPermissionsNudgesProvider />
			</>
		),
	},
	'legacy-project-settings-people': {
		entrypoint: permissionsConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoPermissionsNudgesProvider />
			</>
		),
	},
	'legacy-project-settings-permissions': {
		entrypoint: spacePermissionConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoSpacePermissionNudgesProvider />
			</>
		),
	},
	'servicedesk-getting-started-sla': {
		entrypoint: slaGsConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-sla-edit': {
		entrypoint: slaGsConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-sla-new': {
		entrypoint: slaGsConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-sla-view': {
		entrypoint: slaGsConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-queues': {
		entrypoint: queuesConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-queue-create': {
		entrypoint: queuesConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-queue-edit': {
		entrypoint: queuesConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-request-types': {
		entrypoint: requestTypesConfigProviderEntrypoint,
	},
	'servicedesk-getting-started-request-form': {
		entrypoint: requestTypesConfigProviderEntrypoint,
	},
	'servicedesk-sla-settings': {
		entrypoint: slaConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoSlaNudgesProvider />
			</>
		),
	},
	'global-settings-issues-types-view': {
		entrypoint: globalWorkTypesSettingsConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoNavigationNudgesProvider />
			</>
		),
	},
	'project-settings-software-details': {
		entrypoint: projectSettingsDetailsConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoProjectSettingDetailNudgesProvider />
			</>
		),
	},
	'project-settings-core-details': {
		entrypoint: projectSettingsDetailsConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoProjectSettingDetailNudgesProvider />
			</>
		),
	},
	'software-list': {
		entrypoint: listConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoAiSpaceCreationNudgesProvider />
			</>
		),
	},
	'software-list-classic': {
		entrypoint: listConfigProviderEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoAiSpaceCreationNudgesProvider />
			</>
		),
	},
	'eoc-global-alerts': {
		entrypoint: issueConfigProviderEntrypoint,
	},
	'eoc-global-alert-detail': {
		entrypoint: issueConfigProviderEntrypoint,
	},
	'eoc-global-group-detail': {
		entrypoint: issueConfigProviderEntrypoint,
	},
	'jira-rovo-search': {
		entrypoint: jiraRovoSearchConfigProviderEntrypoint,
		nudges: () => <AiRovoJiraRovoSearchNudgesProvider />,
	},
	'jira-rovo-search-new': {
		entrypoint: jiraRovoSearchConfigProviderEntrypoint,
		nudges: () => <AiRovoJiraRovoSearchNudgesProvider />,
	},
	'servicedesk-schedules': {
		entrypoint: schedulesRovoConfigProviderEntrypoint,
		nudges: () => <AiRovoSchedulesNudgesProvider />,
	},
	'servicedesk-availability': {
		entrypoint: availabilityRovoConfigProviderEntrypoint,
		nudges: () => <AiRovoAvailabilityNudgesProvider />,
	},
	'servicedesk-capacity': {
		entrypoint: capacityRovoConfigProviderEntrypoint,
		nudges: () => <AiRovoCapacityNudgesProvider />,
	},
	'polaris-ideas': {
		entrypoint: jpdProjectViewConfigProviderEntrypoint,
	},
};

export const jiraOnboardingRouteToConfigProvider: Partial<
	Record<
		string,
		{
			entrypoint: RovoEntryPointComponent;
			nudges?: () => React.JSX.Element;
		}
	>
> = {
	...jiraRouteToConfigProvider,
	'software-backlog': {
		entrypoint: onboardingConfigProviderBacklogEntrypoint,
	},
	'software-boards': {
		entrypoint: onboardingConfigProviderBoardEntrypoint,
	},
	'software-list': {
		entrypoint: onboardingConfigProviderListEntrypoint,
		nudges: () => (
			<>
				<AiRovoFomoNudgeProviderDeferring />
				<AiRovoAiSpaceCreationNudgesProvider />
			</>
		),
	},
};

const isOpsPracticeQueuePath = (pathname?: string): boolean =>
	Boolean(
		pathname &&
		/\/section\/(incidents|changes|problems|post-incident-reviews)(\/|$)/.test(pathname),
	);

const getConfiguration = (
	routerState: RouterState,
	isJiraOnboardingVariantEnabled?: boolean,
): {
	entrypoint: RovoEntryPointComponent;
	nudges?: () => React.JSX.Element;
} | null => {
	let configurationKey = routerState.route.name;

	// On the JPD (Jira Product Discovery) ideas project view route, opening an idea adds a
	// `selectedIssue=` query param. Keep the JPD configuration in that case instead of falling
	// through to the generic issue configuration below.
	const isJpdProjectViewWithConversationStarters =
		routerState.route.name === 'polaris-ideas' && fg('rovo_jpd_view_conversation_starters');

	/**
	 * Issue view has special case handling because it can be either a standalone issue view or an embed.
	 * Handling the query parameter in case embded is not set.
	 */
	if (
		!isJpdProjectViewWithConversationStarters &&
		(routerState.route.name === 'issue' ||
			routerState.route.name === 'issue-embed' ||
			routerState.location.search.includes('selectedIssue=') ||
			(routerState.route.name === 'servicedesk-practice-custom-and-issue' &&
				isOpsPracticeQueuePath(routerState.location.pathname)))
	) {
		configurationKey = 'issue';
	}

	// TODO: drop this on FG rovo_jpd_view_conversation_starters removal
	if (configurationKey === 'polaris-ideas' && !fg('rovo_jpd_view_conversation_starters')) {
		return null;
	}

	const routeToConfigProvider = isJiraOnboardingVariantEnabled
		? jiraOnboardingRouteToConfigProvider
		: jiraRouteToConfigProvider;

	if (configurationKey in routeToConfigProvider && routeToConfigProvider[configurationKey]) {
		return routeToConfigProvider[configurationKey] ?? null;
	}
	return null;
};

const useIsFirstTwoWeeksWithGate = functionWithCondition(
	() => fg('jfind-rovo-for-jira-onboarding-gate'),
	useIsFirstTwoWeeks,
	() => false,
);

const useIsRovoForJiraOnboardingEnabledWithGate = functionWithCondition(
	() => fg('jfind-rovo-for-jira-onboarding-gate'),
	useIsRovoForJiraOnboardingEnabled,
	(_isFirstTwoWeeks: boolean | null) => false,
);

export const useAiRovoActiveConfiguration = (): {
	entrypoint: RovoEntryPointComponent;
	nudges?: () => React.JSX.Element;
} | null => {
	const isFirstTwoWeeks = useIsFirstTwoWeeksWithGate();
	const isRovoForJiraOnboardingEligible =
		useIsRovoForJiraOnboardingEnabledWithGate(isFirstTwoWeeks);

	const [configuration, setConfiguration] = useState<{
		entrypoint: RovoEntryPointComponent;
		nudges?: () => React.JSX.Element;
	} | null>(() => {
		// return early to avoid setting the configuration before the trait information is received
		// isFirstTwoWeeks will always be false outside of experiment cohorts
		if (isFirstTwoWeeks === null && fg('jfind-rovo-for-jira-onboarding-gate')) {
			return null;
		}

		return getConfiguration(getRouterState(), isRovoForJiraOnboardingEligible);
	});

	useEffect(() => {
		if (fg('jfind-rovo-for-jira-onboarding-gate')) {
			// Still loading traits — don't set configuration or register listener yet
			if (isFirstTwoWeeks === null) {
				return;
			}

			// Always set configuration for the current page once traits have settled
			setConfiguration(getConfiguration(getRouterState(), isRovoForJiraOnboardingEligible));
		}

		const unsubscribe = addRouteListener((route) => {
			setConfiguration(getConfiguration(route, isRovoForJiraOnboardingEligible));
		});
		return () => unsubscribe();
	}, [isRovoForJiraOnboardingEligible, isFirstTwoWeeks]);

	return configuration;
};
